import React, { useEffect, useRef, useState } from 'react';
import type { ToolUseBlock } from '../../store/chatStore';
import { useChatStore } from '../../store/chatStore';
import { useToolResultContent } from './ToolResultDataContext';
import {
  langFromPath,
  langColor,
  CopyButton,
  Spinner,
  TrafficLights,
  CollapseButton,
  CodePane,
} from './codeBlockUtils';
import { HtmlRenderer } from './HtmlRenderer';
import { UniversalDashboardRenderer } from '../../../../dashboard/components/common/UniversalDashboardRenderer';
import type { SimpleDashboardConfig } from '../../../../dashboard/components/Dashboard/Dashboard';
import { safeJsonParse } from '../../../../utils/jsonRepairUtils';
import { MarkdownWrapper } from './markdown/MarkdownWrapper';

import { useTranslation } from 'react-i18next';

const isPleriHtml = (p: string) =>
  p.endsWith('.html') || p.endsWith('.htm');
const isPleriJson = (p: string) => p.endsWith('.pleri.json');
const isMarkdown = (p: string) => p.endsWith('.md') || p.endsWith('.markdown');

interface ReadResult {
  content: string;
  filePath: string;
  sessionName: string;
  totalLines: number;
  startLine: number;
  endLine: number;
  truncated: boolean;
  preview?: boolean;
}

/** Strip cat -n line number prefix ("     1\t") */
function stripLineNumbers(raw: string): string {
  return raw
    .split('\n')
    .map((line) => line.replace(/^\s*\d+\t/, ''))
    .join('\n');
}

function basename(p: string) {
  return p.split('/').pop() ?? p;
}
function dirname(p: string) {
  return p.includes('/') ? p.slice(0, p.lastIndexOf('/')) : '';
}

/**
 * Renders workspaceReadFile as a collapsible, syntax-highlighted file viewer.
 */
export const ReadFileBlock: React.FC<{ content: ToolUseBlock }> = ({
  content,
}) => {
  const { t } = useTranslation('pleri/chat');
  const [collapsed, setCollapsed] = useState(true);
  const userInteracted = useRef(false);

  const handleCollapseToggle = () => {
    userInteracted.current = true;
    setCollapsed((v) => !v);
  };

  const activeConversationId = useChatStore((s) => s.activeConversationId);
  const isPending = useChatStore((s) =>
    activeConversationId
      ? (
          s.conversations[activeConversationId]?.pendingToolUses ?? new Set()
        ).has(content.id)
      : false,
  );
  const resultJson = useToolResultContent(content.id);

  // `path` is the new param name; `filePath` is the legacy name kept for old conversations
  const filePath =
    typeof content.input?.path === 'string' ? content.input.path :
    typeof content.input?.filePath === 'string' ? content.input.filePath : '';
  const session =
    typeof content.input?.session === 'string' ? content.input.session : 'main';
  const lang = langFromPath(filePath);
  const color = langColor(lang);
  const name = basename(filePath);
  const dir = dirname(filePath);

  const isRunning = isPending || !resultJson;

  let result: ReadResult | null = null;
  let isError = false;
  let errorMessage = '';

  if (resultJson) {
    try {
      const parsed = JSON.parse(resultJson);
      if (parsed?.content != null) {
        result = parsed as ReadResult;
      } else {
        isError = true;
        errorMessage =
          typeof parsed === 'string' ? parsed : JSON.stringify(parsed);
      }
    } catch {
      isError = true;
      errorMessage = resultJson;
    }
  }

  const isPreview = result?.preview === true;

  // Preview mode: stay open to show the rendered result.
  // Normal mode: open while running, collapse when done.
  useEffect(() => {
    if (userInteracted.current) {
      return;
    }
    if (isPreview) {
      setCollapsed(false);
      return;
    }
    if (isRunning) {
      setCollapsed(false);
    } else {
      const t = setTimeout(() => setCollapsed(true), 700);
      return () => clearTimeout(t);
    }
  }, [isRunning, isPreview]);

  const cleanCode =
    result && !isPreview
      ? stripLineNumbers(result.content)
      : (result?.content ?? '');

  const rangeNote = result
    ? result.startLine === 1 && result.endLine === result.totalLines
      ? t('linesTotal', { totalLines: result.totalLines })
      : t('linesOf', {
          startLine: result.startLine,
          endLine: result.endLine,
          totalLines: result.totalLines,
        })
    : null;

  return (
    <div className="my-1 overflow-hidden rounded-xl border border-gray-800 bg-gray-950 text-xs shadow-lg">
      {/* Header */}
      <div className="flex items-center gap-2.5 border-b border-gray-800 bg-gray-900/80 px-3 py-1.5">
        <TrafficLights />

        {/* Language dot */}
        <span
          className="h-2 w-2 flex-shrink-0 rounded-full"
          style={{ background: color }}
        />

        {/* File icon */}
        <svg
          className="h-3.5 w-3.5 flex-shrink-0 text-gray-500"
          fill="none"
          viewBox="0 0 24 24"
          strokeWidth="1.75"
          stroke="currentColor"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            d="M19.5 14.25v-2.625a3.375 3.375 0 00-3.375-3.375h-1.5A1.125 1.125 0 0113.5 7.125v-1.5a3.375 3.375 0 00-3.375-3.375H8.25m0 12.75h7.5m-7.5 3H12M10.5 2.25H5.625c-.621 0-1.125.504-1.125 1.125v17.25c0 .621.504 1.125 1.125 1.125h12.75c.621 0 1.125-.504 1.125-1.125V11.25a9 9 0 00-9-9z"
          />
        </svg>

        {/* Path */}
        <span className="flex-1 truncate font-mono text-xs">
          {dir && <span className="text-gray-500">{dir}/</span>}
          <span className="text-gray-200">{name || filePath}</span>
        </span>

        {/* Meta badges */}
        <div className="flex items-center gap-1.5">
          {rangeNote && (
            <span className="rounded-md bg-gray-800/80 px-1.5 py-0.5 text-xs text-gray-500">
              {rangeNote}
            </span>
          )}
          {session && session !== 'main' && (
            <span className="rounded-md bg-gray-800/80 px-1.5 py-0.5 text-xs text-gray-500">
              {session}
            </span>
          )}
          <span className="rounded-md bg-gray-800/80 px-1.5 py-0.5 text-xs font-medium tracking-wider text-gray-600 uppercase">
            {lang}
          </span>
        </div>

        {/* Status */}
        {isRunning ? (
          <Spinner />
        ) : isError ? (
          <div className="h-2.5 w-2.5 rounded-full bg-red-500" />
        ) : (
          <div className="h-2.5 w-2.5 rounded-full bg-emerald-500" />
        )}

        <CollapseButton collapsed={collapsed} onClick={handleCollapseToggle} />
      </div>
      {!collapsed && (
        <>
          {/* Running state */}
          {isRunning && (
            <div className="flex items-center gap-2 px-4 py-3 font-mono text-xs text-gray-500">
              <span>{t('reading')}</span>
              <span className="text-gray-300">{filePath || '…'}</span>
              <span className="inline-block h-3.5 w-0.5 animate-pulse bg-gray-400" />
            </div>
          )}

          {/* Error */}
          {isError && (
            <div className="px-4 py-3 font-mono text-xs text-red-400">
              {errorMessage}
            </div>
          )}

          {/* Preview or code */}
          {result &&
            isPreview &&
            isPleriJson(filePath) &&
            (() => {
              const title = (filePath.split('/').pop() ?? filePath)
                .replace(/\.pleri\.json$/, '')
                .replace(/[-_]/g, ' ');
              const config = safeJsonParse<SimpleDashboardConfig>(
                result.content,
                'pleri json dashboard',
              );
              return config ? (
                <div className="overflow-hidden rounded-lg border border-gray-800/70">
                  <div className="flex items-center gap-2 border-b border-gray-800/70 bg-gray-900/60 px-3 py-1.5">
                    <svg
                      className="h-3 w-3 flex-shrink-0 text-purple-400/70"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                      strokeWidth="2"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        d="M3 13.125C3 12.504 3.504 12 4.125 12h2.25c.621 0 1.125.504 1.125 1.125v6.75C7.5 20.496 6.996 21 6.375 21h-2.25A1.125 1.125 0 013 19.875v-6.75zM9.75 8.625c0-.621.504-1.125 1.125-1.125h2.25c.621 0 1.125.504 1.125 1.125v11.25c0 .621-.504 1.125-1.125 1.125h-2.25a1.125 1.125 0 01-1.125-1.125V8.625zM16.5 4.125c0-.621.504-1.125 1.125-1.125h2.25C20.496 3 21 3.504 21 4.125v15.75c0 .621-.504 1.125-1.125 1.125h-2.25a1.125 1.125 0 01-1.125-1.125V4.125z"
                      />
                    </svg>
                    <span className="flex-1 truncate font-mono text-xs text-gray-300">
                      {title || name}
                    </span>
                    <span className="text-xs tracking-wider text-purple-400/60 uppercase">
                      {t('dashboard')}
                    </span>
                  </div>
                  <UniversalDashboardRenderer
                    config={config}
                    variant="expandable"
                    enableExpand
                    enablePDF
                    enableScreenshot
                    enableScaling
                    title={title}
                    height="350px"
                    className="my-1"
                    designWidth={1400}
                    maxScale={0.5}
                    minScale={0.25}
                  />
                </div>
              ) : (
                <CodePane
                  code={result.content}
                  lang="json"
                  startLine={1}
                  maxHeight="22rem"
                />
              );
            })()}

          {result && isPreview && isPleriHtml(filePath) && (
            <HtmlRenderer html={result.content} />
          )}

          {result && isPreview && isMarkdown(filePath) && (
            <div className="px-4 py-3">
              <MarkdownWrapper content={result.content} />
            </div>
          )}

          {result && !isPreview && (
            <CodePane
              code={cleanCode}
              lang={lang}
              startLine={result.startLine}
              maxHeight="22rem"
            />
          )}

          {/* Footer — only in non-preview mode */}
          {result && !isPreview && (
            <div className="flex items-center justify-between border-t border-gray-800/60 bg-gray-900/40 px-3 py-1">
              <div className="flex items-center gap-2 text-xs text-gray-600">
                <span className="tracking-wider uppercase">{lang}</span>
                <span>·</span>
                <span>{rangeNote}</span>
                {result.truncated && (
                  <>
                    <span>·</span>
                    <span className="text-yellow-600/80">
                      {t('truncatedUseOffsetLimitToReadMore')}
                    </span>
                  </>
                )}
              </div>
              <CopyButton text={cleanCode} />
            </div>
          )}
        </>
      )}
    </div>
  );
};
